import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Download, Send, Loader2, FileText, ShieldCheck, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import { generateBuyerDemandReport, previewBuyerDemandReport } from '../api/analyticsApi';

// Mirrors DEFAULT_WHY_TEXT in buyer_portal/services/buyer_demand_report.py so the
// agent can read and edit the exact paragraph the vendor will see. Sending it
// unchanged is the same as sending nothing.
export const DEFAULT_WHY_TEXT =
    'An established buyer pool already exists for this type of property. Based on our current '
    + 'database, we have identified a significant number of buyers whose stated requirements align '
    + "with the property's location and likely price range. This gives us the ability to commence "
    + 'buyer engagement immediately rather than waiting until the property is launched to market.';

// Fallback list if the options call fails, so the modal still works. Mirrors
// SECTIONS / DEFAULT_SECTIONS in buyer_demand_report.py.
const FALLBACK_SECTIONS = [
    { key: 'snapshot', label: 'Buyer demand snapshot' },
    { key: 'headline', label: 'Headline sentence' },
    { key: 'why', label: 'Why this matters' },
    { key: 'quality', label: 'Strength of the buyer pool' },
    { key: 'readiness', label: 'Buyer readiness' },
    { key: 'suburbs', label: 'Where these buyers are looking' },
    { key: 'budget', label: 'Buyer demand by budget' },
    { key: 'criteria', label: 'Search criteria used' },
    { key: 'signature', label: 'Your contact details' },
];
const FALLBACK_DEFAULTS = FALLBACK_SECTIONS.map((s) => s.key).filter((k) => k !== 'criteria');

// The sections that carry a list the agent can also prune line by line.
const ROW_SECTIONS = { readiness: 'readiness', suburbs: 'suburbs', budget: 'price_bands' };

const downloadBase64Pdf = (filename, base64) => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'buyer-demand-report.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 4000);
};

const Tick = ({ checked, onChange, label, hint, strong = false }) => (
    <label className="flex items-start gap-2 cursor-pointer select-none">
        <input type="checkbox" checked={checked} onChange={onChange}
            className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-teal-600 focus:ring-teal-400" />
        <span className={`text-xs ${strong ? 'font-medium text-gray-800' : 'text-gray-600'}`}>
            {label}
            {hint ? <span className="ml-1 text-gray-400">{hint}</span> : null}
        </span>
    </label>
);

/**
 * Generate the "Buyer Demand / Buyer Pool" PDF from the current Find Buyers
 * search (Monday 12768121845) — the document an agent takes into a listing
 * presentation to show a vendor the buyer pool already exists.
 *
 * The agent chooses what goes in it before it is made (CG, 11 Aug 2026): every
 * section is a tick box, and the readiness lines, suburbs and budget bands can
 * be pruned individually, so a band like "$1M - $1.25M" can be left off a
 * $1.5M-$2.5M report. The tick boxes are built from a preview call, so they
 * list exactly what this search produced.
 *
 * Two modes:
 *   Vendor   aggregated statistics only. No buyer names, emails or phone numbers
 *            reach the PDF; the backend builds it from counts, so the agent
 *            can't accidentally leave an identity in.
 *   Internal the full ranked list with contact details. Admin only, and never
 *            emailable — it's for the agent's own use.
 */
export default function BuyerDemandReportModal({ criteria, count = 0, propertyLabel = '', isAdmin = false, onClose }) {
    const [mode, setMode] = useState('vendor');
    // Seeded from the Find Buyers search when it was run against a specific
    // address, but always editable — a suburb-only search leaves this blank,
    // and the agent may still be creating the report for a specific listing.
    // Personalises the PDF's title, subtitle and headline sentence once set.
    const [propertyAddress, setPropertyAddress] = useState(propertyLabel || '');
    const [preparedFor, setPreparedFor] = useState('');
    const [whyText, setWhyText] = useState(DEFAULT_WHY_TEXT);
    const [notes, setNotes] = useState('');
    const [emailTo, setEmailTo] = useState('');
    const [busy, setBusy] = useState('');

    const [options, setOptions] = useState(null);
    const [loadingOptions, setLoadingOptions] = useState(true);
    const [sections, setSections] = useState(() => new Set(FALLBACK_DEFAULTS));
    // Rows the agent has UNticked, per section: { readiness: Set, suburbs: Set, price_bands: Set }
    const [hidden, setHidden] = useState({ readiness: new Set(), suburbs: new Set(), price_bands: new Set() });

    const isVendor = mode === 'vendor';

    useEffect(() => {
        let cancelled = false;
        setLoadingOptions(true);
        previewBuyerDemandReport({ ...criteria, property_address: propertyAddress })
            .then((data) => {
                if (cancelled) return;
                setOptions(data);
                setSections(new Set(data.default_sections || FALLBACK_DEFAULTS));
            })
            .catch(() => { /* keep the fallback list; generating still works */ })
            .finally(() => { if (!cancelled) setLoadingOptions(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sectionList = options?.sections?.length ? options.sections : FALLBACK_SECTIONS;

    const toggleSection = useCallback((key) => {
        setSections((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    }, []);

    const toggleRow = useCallback((bucket, key) => {
        setHidden((prev) => {
            const next = new Set(prev[bucket]);
            if (next.has(key)) next.delete(key); else next.add(key);
            return { ...prev, [bucket]: next };
        });
    }, []);

    const payloadOptions = useMemo(() => ({
        sections: Array.from(sections),
        hidden_readiness: Array.from(hidden.readiness),
        hidden_suburbs: Array.from(hidden.suburbs),
        hidden_price_bands: Array.from(hidden.price_bands),
    }), [sections, hidden]);

    const run = async (action) => {
        setBusy(action);
        try {
            const data = await generateBuyerDemandReport({
                ...criteria,
                ...payloadOptions,
                mode,
                property_address: propertyAddress.trim(),
                prepared_for: preparedFor.trim(),
                why_text: isVendor ? whyText.trim() : '',
                notes: notes.trim(),
                ...(action === 'email' ? { email_to: emailTo.trim() } : {}),
            });
            if (action === 'email') {
                toast.success(`Report emailed to ${data.recipient}`);
                onClose();
            } else {
                downloadBase64Pdf(data.filename, data.pdf_base64);
                toast.success(`${isVendor ? 'Buyer demand report' : 'Internal buyer report'} downloaded`);
            }
        } catch (error) {
            toast.error(error?.response?.data?.error || 'Could not generate the report.');
        } finally {
            setBusy('');
        }
    };

    // The tick-box list for a section that has individual rows (readiness /
    // suburbs / budget bands), shown indented under its section.
    const renderRows = (sectionKey) => {
        const bucket = ROW_SECTIONS[sectionKey];
        if (!bucket || !sections.has(sectionKey)) return null;
        const rows = options?.[bucket] || [];
        if (!rows.length) return null;
        return (
            <div className="ml-5 mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 border-l border-gray-100 pl-3">
                {rows.map((row) => (
                    <Tick key={row.key}
                        checked={!hidden[bucket].has(row.key)}
                        onChange={() => toggleRow(bucket, row.key)}
                        label={row.label}
                        hint={bucket === 'readiness' ? '' : `· ${row.count}`} />
                ))}
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onMouseDown={onClose}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[92vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
                <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 text-teal-600 shrink-0" />
                        <span className="text-sm font-semibold text-gray-900 truncate">
                            Buyer demand report · {count} buyer{count !== 1 ? 's' : ''}
                        </span>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {isAdmin && (
                        <div>
                            <span className="block text-xs font-semibold text-gray-600 mb-1.5">Report type</span>
                            <div className="flex gap-1.5">
                                <button type="button" onClick={() => setMode('vendor')}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${isVendor ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                                    Vendor report
                                </button>
                                <button type="button" onClick={() => setMode('internal')}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${!isVendor ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                                    <Lock className="w-3 h-3" /> Internal report
                                </button>
                            </div>
                            <p className="text-[11px] text-gray-500 mt-1.5">
                                {isVendor
                                    ? 'Anonymised statistics for a listing presentation. Safe to send a vendor.'
                                    : 'Full buyer list with names and contact details. Admin only, and it can’t be emailed from here.'}
                            </p>
                        </div>
                    )}

                    {/* What goes in the PDF. Ticked = shown. */}
                    <div className="rounded-lg border border-gray-200 p-3">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-gray-600">What to include</span>
                            {loadingOptions && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
                        </div>
                        <div className="space-y-1.5">
                            {sectionList.map((section) => (
                                <div key={section.key}>
                                    <Tick strong
                                        checked={sections.has(section.key)}
                                        onChange={() => toggleSection(section.key)}
                                        label={section.label} />
                                    {renderRows(section.key)}
                                </div>
                            ))}
                        </div>
                        <p className="text-[11px] text-gray-400 mt-2">
                            Untick anything this vendor doesn’t need. The report always carries the logo and the
                            privacy disclaimer, and is fitted to two pages.
                        </p>
                    </div>

                    <label className="block">
                        <span className="block text-xs font-semibold text-gray-600 mb-1">Property address (optional)</span>
                        <input type="text" value={propertyAddress} onChange={(e) => setPropertyAddress(e.target.value)}
                            placeholder="12 Smith Street, Northcote"
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-400 focus:ring-1 focus:ring-teal-400" />
                        <span className="block text-[11px] text-gray-400 mt-1">
                            Personalises the report's title and headline. Leave blank for a suburb-only report.
                        </span>
                    </label>

                    <label className="block">
                        <span className="block text-xs font-semibold text-gray-600 mb-1">Prepared for (optional)</span>
                        <input type="text" value={preparedFor} onChange={(e) => setPreparedFor(e.target.value)}
                            placeholder="Mr & Mrs Smith"
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-400 focus:ring-1 focus:ring-teal-400" />
                    </label>

                    {isVendor && sections.has('why') && (
                        <label className="block">
                            <span className="block text-xs font-semibold text-gray-600 mb-1">“Why this matters” wording</span>
                            <textarea value={whyText} onChange={(e) => setWhyText(e.target.value)} rows={5}
                                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-400 focus:ring-1 focus:ring-teal-400" />
                            <span className="block text-[11px] text-gray-400 mt-1">
                                This paragraph appears near the top of the vendor report. Edit it for this report, or
                                <button type="button" onClick={() => setWhyText(DEFAULT_WHY_TEXT)}
                                    className="ml-1 font-medium text-teal-600 hover:text-teal-700">reset to the default</button>.
                            </span>
                        </label>
                    )}

                    <label className="block">
                        <span className="block text-xs font-semibold text-gray-600 mb-1">Your notes (optional)</span>
                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                            placeholder="Why we believe this property will appeal to our database…"
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-400 focus:ring-1 focus:ring-teal-400" />
                    </label>

                    {isVendor && (
                        <label className="block">
                            <span className="block text-xs font-semibold text-gray-600 mb-1">Email it to the vendor (optional)</span>
                            <input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                                placeholder="vendor@example.com"
                                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-400 focus:ring-1 focus:ring-teal-400" />
                        </label>
                    )}

                    <p className="flex items-start gap-1.5 text-[11px] text-gray-500">
                        {isVendor ? (
                            <>
                                <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" />
                                Buyer names, emails and phone numbers are never included in the vendor report. It is
                                built from aggregate numbers only.
                            </>
                        ) : (
                            <>
                                <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-500" />
                                This copy contains buyer contact details. Don’t send it to a vendor.
                            </>
                        )}
                    </p>
                </div>

                <div className="flex-shrink-0 flex flex-wrap items-center justify-end gap-2 px-4 py-3 border-t border-gray-100">
                    <button onClick={onClose} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
                    <button onClick={() => run('download')} disabled={!!busy}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                        {busy === 'download' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        Download PDF
                    </button>
                    {isVendor && (
                        <button onClick={() => run('email')} disabled={!!busy || !emailTo.trim()}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-50">
                            {busy === 'email' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            Email to vendor
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
